import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Music, Upload, BarChart3, Settings, ArrowRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { motion } from 'framer-motion';

export default function Dashboard() {
  const { profile } = useAuth();
  const [planLimits, setPlanLimits] = React.useState<{ api_calls_limit: number; monthly_space_limit: number } | null>(null);

  React.useEffect(() => {
    const fetchPlanLimits = async () => {
      if (!profile) return;
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('api_calls_limit, monthly_space_limit')
        .eq('name', profile.subscription_plan)
        .single();
      if (!error && data) {
        setPlanLimits({
          api_calls_limit: data.api_calls_limit,
          monthly_space_limit: data.monthly_space_limit,
        });
      }
    };
    fetchPlanLimits();
  }, [profile]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 100,
      },
    },
  };

  if (!profile || !planLimits) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-gray-50 to-blue-100">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-100">
      <div className="max-w-7xl mx-auto p-6 sm:p-8">
        <motion.div
          variants={itemVariants}
          initial="hidden"
          animate="visible"
          className="mb-10"
        >
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-3">
            Welcome back, {profile?.full_name || 'User'}!
          </h1>
          <p className="text-lg text-gray-600">
            Here's a summary of your account and activity.
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8"
        >
          <motion.div variants={itemVariants}>
            <Card className="shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">API Calls Used</CardTitle>
                <BarChart3 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{profile.api_calls_used || 0}</div>
                <p className="text-xs text-muted-foreground">
                  of {planLimits.api_calls_limit} this month
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Card className="shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Storage Used</CardTitle>
                <Upload className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {((profile?.monthly_space_used || 0) / 1024 / 1024).toFixed(1)} MB
                </div>
                <p className="text-xs text-muted-foreground">
                  of {(planLimits.monthly_space_limit / 1024 / 1024) >= 1024 ? `${(planLimits.monthly_space_limit / 1024 / 1024 / 1024).toFixed(1)} GB` : `${(planLimits.monthly_space_limit / 1024 / 1024).toFixed(1)} MB`} limit
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Card className="shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Subscription</CardTitle>
                <Settings className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold capitalize">
                  {profile?.subscription_plan || 'Free'}
                </div>
                <p className="text-xs text-muted-foreground">
                  Current plan
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>


        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          <motion.div variants={itemVariants}>
            <Card className="shadow-lg hover:shadow-xl transition-shadow bg-gradient-to-r from-purple-600 to-blue-600 text-white">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Music className="h-5 w-5" />
                  <span>Extract Music</span>
                </CardTitle>
                <CardDescription className="text-purple-200">
                  Upload a video file or provide a YouTube URL to get started.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Link to="/extract">
                  <Button className="w-full bg-white text-purple-600 hover:bg-gray-100 group">
                    Start Extracting <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Card className="shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Settings className="h-5 w-5 text-purple-600" />
                  <span>Account Settings</span>
                </CardTitle>
                <CardDescription>
                  Manage your profile, subscription, and preferences.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Link to="/profile">
                  <Button variant="outline" className="w-full group">
                    View Profile <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>

        {profile?.role === 'admin' && (
          <motion.div variants={itemVariants} className="mt-8">
            <Card className="border-orange-200 bg-orange-50 shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2 text-orange-800">
                  <Settings className="h-5 w-5" />
                  <span>Admin Panel</span>
                </CardTitle>
                <CardDescription className="text-orange-600">
                  You have administrator privileges.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Link to="/admin">
                  <Button variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-100 group">
                    Access Admin Panel <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
}
